import React, { useState, useEffect, useMemo } from "react";
import {
  Share2,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  Plus,
  Trash2,
  ExternalLink,
  RefreshCw,
  Search,
  Link2,
  Settings,
  Plug,
  Copy,
  Eye,
  Calendar,
  Globe,
  PenTool,
  Code,
  Hash,
  BookOpen,
  Mail,
  Briefcase,
  ThumbsUp,
  AtSign,
  Image,
  MessageCircle,
  Terminal,
  TrendingUp,
  Bookmark,
  Layers,
  Pocket,
  Shield,
  Zap,
  Wand2,
  FileText,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Edit3,
  Save,
  X,
  HelpCircle,
  Info,
  ClipboardCopy,
} from "lucide-react";
import api from "../api/client";

const TIER_LABELS = {
  1: { label: "T1", color: "text-green-400", bg: "bg-green-900/20" },
  2: { label: "T2", color: "text-blue-400", bg: "bg-blue-900/20" },
  3: { label: "T3", color: "text-gray-400", bg: "bg-gray-800/40" },
};

const ICON_MAP = {
  "pen-tool": PenTool, code: Code, hash: Hash, "book-open": BookOpen,
  globe: Globe, mail: Mail, briefcase: Briefcase, "thumbs-up": ThumbsUp,
  "at-sign": AtSign, image: Image, "message-circle": MessageCircle,
  terminal: Terminal, "trending-up": TrendingUp, bookmark: Bookmark,
  layers: Layers, pocket: Pocket,
};

const POST_STATUS = {
  draft: { label: "Draft", color: "text-gray-400", bg: "bg-gray-800/40" },
  scheduled: { label: "Scheduled", color: "text-blue-400", bg: "bg-blue-900/20" },
  publishing: { label: "Publishing", color: "text-amber-400", bg: "bg-amber-900/20" },
  published: { label: "Published", color: "text-green-400", bg: "bg-green-900/20" },
  failed: { label: "Failed", color: "text-red-400", bg: "bg-red-900/20" },
};

// Platform-specific help text for connecting accounts
const PLATFORM_HELP = {
  // Blog platforms with API
  blogger: {
    placeholder: "https://yourblog.blogspot.com",
    help: "Enter your Blogger blog URL. You'll sign in with Google to authorize OnesuiteSEO to publish posts.",
  },
  "dev-to": {
    placeholder: "Your dev.to username",
    help: "Go to dev.to → Settings → Extensions → DEV Community API Keys → Generate API Key.",
    apiKeyLabel: "DEV API Key",
    tokenLabel: "",
  },
  hashnode: {
    placeholder: "Your Hashnode username",
    help: "Go to hashnode.com → Settings → Developer → Personal Access Tokens → Generate. You also need your Publication ID.",
    apiKeyLabel: "",
    tokenLabel: "Personal Access Token",
    extraFields: [{ key: "publication_id", label: "Publication ID (required)", placeholder: "Found in Hashnode Dashboard → Settings → General" }],
  },
  tumblr: {
    placeholder: "https://www.tumblr.com/blog/yourblogname",
    help: "Enter your Tumblr blog URL. You'll be redirected to Tumblr to authorize OnesuiteSEO to publish posts.",
  },
  // Blog platforms — manual
  medium: {
    placeholder: "https://medium.com/@yourusername",
    help: "Manual platform — Medium's API is deprecated. Content is generated for you to copy and paste on Medium directly.",
  },
  "wordpress-com": {
    placeholder: "https://yourblog.wordpress.com",
    help: "Manual platform — copy the generated content and paste it in your WordPress.com editor.",
  },
  substack: {
    placeholder: "https://yourblog.substack.com",
    help: "Manual platform — copy and paste content into your Substack dashboard.",
  },
  hubpages: {
    placeholder: "Your HubPages username",
    help: "Manual platform — copy content into a new Hub on HubPages.",
  },
  "vocal-media": {
    placeholder: "Your Vocal username",
    help: "Manual platform — copy content and submit as a new story on vocal.media.",
  },
  // Social platforms — all manual
  linkedin: {
    placeholder: "https://linkedin.com/in/yourname",
    help: "Manual — copy content and post as a LinkedIn Article from your profile.",
  },
  facebook: {
    placeholder: "Your Page name or URL",
    help: "Manual — copy content and paste into a Facebook post or note on your Page.",
  },
  "twitter-x": {
    placeholder: "@yourhandle",
    help: "Manual — copy the generated text and tweet it from X/Twitter.",
  },
  pinterest: {
    placeholder: "Your Pinterest profile URL",
    help: "Manual — create a new Pin with the generated content and link back to your site.",
  },
  quora: {
    placeholder: "https://quora.com/profile/yourname",
    help: "Manual — find relevant questions and post the generated answer with a link to your site.",
  },
  houzz: {
    placeholder: "Your Houzz professional profile URL",
    help: "Manual — post content in Houzz discussions or your professional profile. Great for contractors.",
  },
  // Aggregator platforms — all manual
  reddit: {
    placeholder: "u/yourusername",
    help: "Manual — find relevant subreddits and post the generated content. Follow subreddit rules.",
  },
  "hacker-news": {
    placeholder: "Your HN username",
    help: "Manual — submit the generated story/link on news.ycombinator.com.",
  },
  growthhackers: {
    placeholder: "Your GrowthHackers username",
    help: "Manual — submit the article as a new post on GrowthHackers.",
  },
  instructables: {
    placeholder: "Your Instructables username",
    help: "Manual — create a new Instructable with the generated DIY/how-to content.",
  },
  ezinearticles: {
    placeholder: "Your EzineArticles author name",
    help: "Manual — submit the article through EzineArticles' author dashboard for dofollow links.",
  },
  // Bookmarking — all manual
  mix: {
    placeholder: "Your Mix username",
    help: "Manual — bookmark your page URL on Mix for content curation.",
  },
  flipboard: {
    placeholder: "Your Flipboard magazine URL",
    help: "Manual — flip your article into a Flipboard magazine for dofollow backlink.",
  },
  pocket: {
    placeholder: "Your Pocket username",
    help: "Manual — save your page URL to Pocket for discovery.",
  },
  // Site builders — manual
  "google-sites": {
    placeholder: "https://sites.google.com/view/yoursite",
    help: "Manual — create a new Google Site and paste the generated content. High DA dofollow links.",
  },
  weebly: {
    placeholder: "https://yoursite.weebly.com",
    help: "Manual — create a free Weebly site and paste the generated content for dofollow backlinks.",
  },
  wix: {
    placeholder: "https://yoursite.wixsite.com",
    help: "Manual — create a free Wix site and paste the generated content for dofollow backlinks.",
  },
};

function StatCard({ label, value, sub, icon: Icon, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 rounded-xl border border-dark-50 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={16} className={color} />}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

const TABS = [
  { key: "posts", label: "Posts", icon: Send },
  { key: "accounts", label: "Accounts", icon: Plug },
  { key: "schedules", label: "Schedule", icon: Calendar },
];

/* ======== WIZARD: AI Content Generation ======== */
function ContentWizard({ siteId, accounts, onCreated, onClose }) {
  const [step, setStep] = useState(1);
  // Step 1: Platform
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  // Step 2: Content Source
  const [sourceType, setSourceType] = useState("repurpose");
  const [sourcePages, setSourcePages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageSearch, setPageSearch] = useState("");
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  // Step 3: Generate
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  // Step 4: Preview/Edit
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  // Step 5: Publish
  const [scheduledFor, setScheduledFor] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  // Load source pages and keywords
  useEffect(() => {
    if (!siteId) return;
    setLoadingPages(true);
    Promise.all([
      api.get(`/websites/${siteId}/syndication/source-pages/`),
      api.get(`/websites/${siteId}/keywords/?priority=high`).catch(() => ({ data: [] })),
    ])
      .then(([pagesRes, kwRes]) => {
        setSourcePages(pagesRes.data || []);
        setKeywords((kwRes.data.results || kwRes.data || []).slice(0, 20));
      })
      .catch(() => {})
      .finally(() => setLoadingPages(false));
  }, [siteId]);

  const filteredPages = useMemo(() => {
    if (!pageSearch) return sourcePages.slice(0, 30);
    return sourcePages.filter(
      (p) => p.title?.toLowerCase().includes(pageSearch.toLowerCase()) ||
             p.url?.toLowerCase().includes(pageSearch.toLowerCase())
    ).slice(0, 30);
  }, [sourcePages, pageSearch]);

  const handleGenerate = () => {
    if (selectedAccounts.length === 0) return;
    const acc = accounts.find((a) => a.id === selectedAccounts[0]);
    if (!acc) return;

    setGenerating(true);
    api.post(`/websites/${siteId}/syndication/generate/`, {
      platform_id: acc.platform,
      account_id: acc.id,
      source_type: sourceType,
      source_url: sourceType === "repurpose" ? selectedPage?.url : null,
      topic: sourceType === "new" ? topic : null,
      keyword: keyword || null,
    })
      .then((res) => {
        setGeneratedContent(res.data);
        setEditTitle(res.data.title || "");
        setEditContent(res.data.content || "");
        setStep(4);
      })
      .catch((err) => {
        const msg = err.response?.data?.error || "Generation failed";
        alert(msg);
      })
      .finally(() => setGenerating(false));
  };

  const handleCreate = (postStatus) => {
    if (selectedAccounts.length === 0) return;
    setCreating(true);

    const promises = selectedAccounts.map((accId) => {
      const payload = {
        account: accId,
        title: editTitle,
        content: editContent,
        source_url: generatedContent?.backlink_url || selectedPage?.url || "",
        canonical_url: generatedContent?.canonical_url || selectedPage?.url || "",
        anchor_text: generatedContent?.anchor_text || "",
        status: postStatus,
      };
      if (postStatus === "scheduled" && scheduledFor) {
        payload.scheduled_for = scheduledFor;
      }
      return api.post(`/websites/${siteId}/syndication/posts/`, payload);
    });

    Promise.all(promises)
      .then((results) => {
        setCreated(true);
        onCreated(results.map((r) => r.data));
        setTimeout(() => onClose(), 1500);
      })
      .catch(() => {})
      .finally(() => setCreating(false));
  };

  const canProceed = () => {
    if (step === 1) return selectedAccounts.length > 0;
    if (step === 2) {
      if (sourceType === "repurpose") return !!selectedPage;
      return topic.trim().length > 0 || keyword.trim().length > 0;
    }
    return true;
  };

  if (created) {
    return (
      <div className="bg-dark-300 rounded-xl border border-copper/30 p-8 text-center">
        <CheckCircle size={48} className="text-green-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-white mb-1">Posts Created!</h3>
        <p className="text-sm text-gray-500">
          {selectedAccounts.length} post{selectedAccounts.length > 1 ? "s" : ""} created successfully.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-dark-300 rounded-xl border border-copper/30 p-6 space-y-4">
      {/* Wizard header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wand2 size={20} className="text-copper" />
          <h3 className="text-sm font-semibold">AI Content Wizard</h3>
          <div className="flex items-center gap-1 ml-4">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className={`w-6 h-1 rounded-full transition-colors ${
                s <= step ? "bg-copper" : "bg-dark-50"
              }`} />
            ))}
          </div>
          <span className="text-xs text-gray-500 ml-2">Step {step}/5</span>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
          <X size={16} />
        </button>
      </div>

      {/* Step 1: Choose Platform */}
      {step === 1 && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium text-white mb-1">Choose Platform</h4>
            <p className="text-xs text-gray-500">Select which account(s) to publish to</p>
          </div>
          {accounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No accounts connected. Go to the Accounts tab first.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {accounts.map((acc) => {
                const isSelected = selectedAccounts.includes(acc.id);
                return (
                  <button
                    key={acc.id}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedAccounts((prev) => prev.filter((id) => id !== acc.id));
                      } else {
                        setSelectedAccounts((prev) => [...prev, acc.id]);
                      }
                    }}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? "border-copper/50 bg-copper/10"
                        : "border-dark-50 hover:border-gray-600"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isSelected ? "bg-copper/20" : "bg-dark"
                    }`}>
                      {isSelected ? <CheckCircle size={16} className="text-copper" /> : <Globe size={16} className="text-gray-500" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium">{acc.platform_name}</span>
                      <span className="text-xs text-gray-500 block">{acc.account_name}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Content Source */}
      {step === 2 && (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-white mb-1">Content Source</h4>
            <p className="text-xs text-gray-500">Choose how to create the content</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setSourceType("repurpose")}
              className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-colors ${
                sourceType === "repurpose"
                  ? "border-copper/50 bg-copper/10 text-copper"
                  : "border-dark-50 text-gray-400 hover:border-gray-600"
              }`}
            >
              <RefreshCw size={16} className="mx-auto mb-1" />
              Repurpose from website
            </button>
            <button
              onClick={() => setSourceType("new")}
              className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-colors ${
                sourceType === "new"
                  ? "border-copper/50 bg-copper/10 text-copper"
                  : "border-dark-50 text-gray-400 hover:border-gray-600"
              }`}
            >
              <Sparkles size={16} className="mx-auto mb-1" />
              Generate new content
            </button>
          </div>

          {sourceType === "repurpose" && (
            <div className="space-y-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text" placeholder="Search pages..."
                  value={pageSearch} onChange={(e) => setPageSearch(e.target.value)}
                  className="w-full bg-dark border border-dark-50 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {loadingPages ? (
                  <div className="text-center py-4">
                    <Loader size={16} className="animate-spin text-copper mx-auto" />
                  </div>
                ) : filteredPages.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">No pages found. Run a site crawl first.</p>
                ) : (
                  filteredPages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => setSelectedPage(page)}
                      className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                        selectedPage?.id === page.id
                          ? "bg-copper/10 border border-copper/30"
                          : "hover:bg-dark-50/30 border border-transparent"
                      }`}
                    >
                      <div className="font-medium text-white truncate">{page.title || "Untitled"}</div>
                      <div className="text-gray-500 truncate">{page.url}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {sourceType === "new" && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Topic</label>
                <input type="text" placeholder="e.g., Bathroom renovation tips for homeowners"
                  value={topic} onChange={(e) => setTopic(e.target.value)}
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
              </div>
              {keywords.length > 0 && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Or pick a priority keyword</label>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {keywords.map((kw) => (
                      <button
                        key={kw.id}
                        onClick={() => setKeyword(kw.keyword)}
                        className={`px-2 py-1 rounded text-xs transition-colors ${
                          keyword === kw.keyword
                            ? "bg-copper/20 text-copper border border-copper/30"
                            : "bg-dark text-gray-400 border border-dark-50 hover:border-gray-600"
                        }`}
                      >
                        {kw.keyword}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Target Keyword (optional)</label>
            <input type="text" placeholder="e.g., bathroom renovation westchester"
              value={keyword} onChange={(e) => setKeyword(e.target.value)}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          </div>
        </div>
      )}

      {/* Step 3: Generating */}
      {step === 3 && (
        <div className="text-center py-12">
          <Loader size={32} className="animate-spin text-copper mx-auto mb-4" />
          <h4 className="text-sm font-medium text-white mb-1">Generating Content...</h4>
          <p className="text-xs text-gray-500">
            AI is creating platform-optimized content with natural backlinks.
          </p>
          <p className="text-xs text-gray-600 mt-2">This usually takes 10-30 seconds.</p>
        </div>
      )}

      {/* Step 4: Preview & Edit */}
      {step === 4 && generatedContent && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-white mb-1">Preview & Edit</h4>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{generatedContent.platform_name}</span>
                <span>{generatedContent.word_count} words</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Title</label>
            <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none" />
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Content</label>
            <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)}
              rows={10}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-copper/50 focus:outline-none resize-y" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Backlink URL</label>
              <div className="text-xs text-copper truncate">{generatedContent.backlink_url}</div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Anchor Text</label>
              <div className="text-xs text-white">{generatedContent.anchor_text}</div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-0.5 block">Canonical URL</label>
              <div className="text-xs text-gray-400 truncate">{generatedContent.canonical_url}</div>
            </div>
          </div>
        </div>
      )}

      {/* Step 5: Publish options */}
      {step === 5 && (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-white mb-1">Publish Options</h4>
            <p className="text-xs text-gray-500">
              Choose when to publish to {selectedAccounts.length} platform{selectedAccounts.length > 1 ? "s" : ""}
            </p>
          </div>

          <div className="space-y-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Schedule for later (optional)</label>
              <input type="datetime-local" value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none" />
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => handleCreate("draft")} disabled={creating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-dark border border-dark-50 text-gray-300 rounded-lg text-sm font-medium hover:border-gray-600 transition-colors disabled:opacity-50">
              <Save size={16} /> Save as Draft
            </button>
            {scheduledFor ? (
              <button onClick={() => handleCreate("scheduled")} disabled={creating}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
                {creating ? <Loader size={16} className="animate-spin" /> : <Clock size={16} />}
                Schedule
              </button>
            ) : (
              <button onClick={() => handleCreate("draft")} disabled={creating}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 transition-colors disabled:opacity-50">
                {creating ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                Create Post
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      {step !== 3 && !created && (
        <div className="flex items-center justify-between pt-2 border-t border-dark-50">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < 5 && (
            <button
              onClick={() => {
                if (step === 2) {
                  // Go to step 3 (generating) then auto-generate
                  setStep(3);
                  handleGenerate();
                } else {
                  setStep(step + 1);
                }
              }}
              disabled={!canProceed()}
              className="flex items-center gap-1 px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 disabled:opacity-50 transition-colors"
            >
              {step === 2 ? (
                <>
                  <Sparkles size={14} /> Generate Content
                </>
              ) : (
                <>
                  Next <ArrowRight size={14} />
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ======== TAB: POSTS ======== */
function PostsTab({ siteId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showWizard, setShowWizard] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [publishing, setPublishing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchData = () => {
    const statusParam = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    Promise.all([
      api.get(`/websites/${siteId}/syndication/posts/${statusParam}`),
      api.get(`/websites/${siteId}/syndication/summary/`),
      api.get(`/websites/${siteId}/syndication/accounts/`),
    ])
      .then(([postsRes, summaryRes, accountsRes]) => {
        setPosts(postsRes.data.results || postsRes.data);
        setSummary(summaryRes.data);
        setAccounts(accountsRes.data.results || accountsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    fetchData();
  }, [siteId, statusFilter]);

  const handlePublish = (id) => {
    setPublishing(id);
    api.post(`/websites/${siteId}/syndication/posts/${id}/publish/`)
      .then(() => {
        const poll = setInterval(() => {
          api.get(`/websites/${siteId}/syndication/posts/${id}/`)
            .then((res) => {
              if (res.data.status !== "publishing") {
                clearInterval(poll);
                setPublishing(null);
                setPosts((prev) => prev.map((p) => p.id === id ? res.data : p));
              }
            })
            .catch(() => { clearInterval(poll); setPublishing(null); });
        }, 3000);
        setTimeout(() => { clearInterval(poll); setPublishing(null); fetchData(); }, 60000);
      })
      .catch(() => setPublishing(null));
  };

  const handleRetry = (id) => {
    setPublishing(id);
    api.post(`/websites/${siteId}/syndication/posts/${id}/retry/`)
      .then(() => {
        setTimeout(() => { setPublishing(null); fetchData(); }, 5000);
      })
      .catch(() => setPublishing(null));
  };

  const handleDelete = (id) => {
    api.delete(`/websites/${siteId}/syndication/posts/${id}/`).then(() => {
      setPosts((prev) => prev.filter((p) => p.id !== id));
    });
  };

  const handleCopy = (post) => {
    const textContent = post.content.replace(/<[^>]+>/g, "");
    navigator.clipboard.writeText(`${post.title}\n\n${textContent}`).then(() => {
      setCopiedId(post.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleWizardCreated = (newPosts) => {
    setPosts((prev) => [...newPosts, ...prev]);
    fetchData();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading posts...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Posts" value={summary.total_posts} icon={Send} />
          <StatCard label="Published" value={summary.published} icon={CheckCircle} color="text-green-400" />
          <StatCard label="Scheduled" value={summary.scheduled} icon={Clock} color="text-blue-400" />
          <StatCard label="Failed" value={summary.failed} icon={XCircle} color="text-red-400" />
        </div>
      )}

      {/* Filters + Create */}
      <div className="flex items-center justify-between">
        <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden">
          {[{ key: "all", label: "All" }, { key: "draft", label: "Draft" }, { key: "scheduled", label: "Scheduled" }, { key: "published", label: "Published" }, { key: "failed", label: "Failed" }].map((f) => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === f.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowWizard(!showWizard)}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 transition-colors">
          <Wand2 size={16} /> {showWizard ? "Close Wizard" : "New Post"}
        </button>
      </div>

      {/* AI Content Wizard */}
      {showWizard && (
        <ContentWizard
          siteId={siteId}
          accounts={accounts}
          onCreated={handleWizardCreated}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Posts list */}
      <div className="space-y-3">
        {posts.length === 0 ? (
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
            <Send size={32} className="mx-auto mb-3 text-gray-600" />
            <p>No syndication posts yet.</p>
            <p className="text-xs mt-1">Click "New Post" to create AI-generated content for your platforms.</p>
          </div>
        ) : posts.map((post) => {
          const st = POST_STATUS[post.status] || POST_STATUS.draft;
          return (
            <div key={post.id} className="bg-dark-300 rounded-xl border border-dark-50 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
                    <span className="text-xs text-gray-500">{post.platform_name}</span>
                    <span className="text-xs text-gray-600">via {post.account_name}</span>
                  </div>
                  <h3 className="text-sm font-medium text-white mb-1">{post.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{post.content.replace(/<[^>]+>/g, "").slice(0, 200)}</p>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-600 flex-wrap">
                    {post.source_url && <span>Source: {post.source_url}</span>}
                    {post.anchor_text && <span className="text-copper/60">Anchor: "{post.anchor_text}"</span>}
                    {post.canonical_url && <span>Canonical: {post.canonical_url}</span>}
                    {post.scheduled_for && <span>Scheduled: {new Date(post.scheduled_for).toLocaleString()}</span>}
                    {post.published_at && <span>Published: {new Date(post.published_at).toLocaleString()}</span>}
                    {post.error_message && <span className="text-red-400">Error: {post.error_message}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-3">
                  {/* Copy button for manual platforms */}
                  <button onClick={() => handleCopy(post)}
                    className="p-1.5 text-gray-500 hover:text-copper transition-colors" title="Copy content">
                    {copiedId === post.id ? <CheckCircle size={14} className="text-green-400" /> : <ClipboardCopy size={14} />}
                  </button>
                  {post.platform_url && (
                    <a href={post.platform_url} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-gray-500 hover:text-copper transition-colors" title="View on platform">
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {(post.status === "draft" || post.status === "scheduled") && (
                    <button onClick={() => handlePublish(post.id)} disabled={publishing === post.id}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors disabled:opacity-50">
                      {publishing === post.id ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                      Publish
                    </button>
                  )}
                  {post.status === "failed" && (
                    <button onClick={() => handleRetry(post.id)} disabled={publishing === post.id}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-amber-900/30 text-amber-400 rounded hover:bg-amber-900/50 transition-colors disabled:opacity-50">
                      {publishing === post.id ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      Retry
                    </button>
                  )}
                  <button onClick={() => handleDelete(post.id)}
                    className="p-1.5 text-gray-600 hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ======== TAB: ACCOUNTS ======== */
function AccountsTab({ siteId }) {
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(null);
  const [connectForm, setConnectForm] = useState({ account_name: "", api_key: "", access_token: "", extraSettings: {} });
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/syndication/platforms/`),
      api.get(`/websites/${siteId}/syndication/accounts/`),
    ])
      .then(([platRes, accRes]) => {
        setPlatforms(platRes.data.results || platRes.data);
        setAccounts(accRes.data.results || accRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleConnect = (platformId, platformSlug) => {
    if (!connectForm.account_name) return;
    setConnecting(true);
    const needsOAuth = platformSlug === "blogger" || platformSlug === "tumblr";
    const payload = {
      platform: platformId,
      account_name: connectForm.account_name,
      api_key: connectForm.api_key,
      access_token: connectForm.access_token,
      is_connected: !needsOAuth, // OAuth platforms need auth flow first
    };
    // For Tumblr: include api_secret from extraSettings
    if (platformSlug === "tumblr" && connectForm.extraSettings?.api_secret) {
      payload.api_secret = connectForm.extraSettings.api_secret;
    }
    // Pass extra settings (e.g. publication_id for Hashnode, blog_id for Blogger)
    const extraSettingsCopy = { ...(connectForm.extraSettings || {}) };
    delete extraSettingsCopy.api_secret; // Don't store api_secret in settings JSON
    if (Object.keys(extraSettingsCopy).length > 0) {
      payload.settings = extraSettingsCopy;
    }
    api.post(`/websites/${siteId}/syndication/accounts/`, payload)
      .then((res) => {
        setAccounts((prev) => [...prev, res.data]);
        setShowConnect(null);
        setConnectForm({ account_name: "", api_key: "", access_token: "", extraSettings: {} });

        // For OAuth platforms: immediately start OAuth flow after account creation
        if (platformSlug === "blogger") {
          api.post(`/websites/${siteId}/syndication/blogger/${res.data.id}/connect/`)
            .then((oauthRes) => {
              if (oauthRes.data.auth_url) {
                const popup = window.open(oauthRes.data.auth_url, "_blank", "width=600,height=700");
                const check = setInterval(() => {
                  if (!popup || popup.closed) {
                    clearInterval(check);
                    api.get(`/websites/${siteId}/syndication/accounts/`)
                      .then((r) => setAccounts(r.data.results || r.data));
                  }
                }, 1000);
              }
            })
            .catch((err) => alert(err.response?.data?.error || "Account created. Click 'Connect with Google' to authorize."));
        }
        if (platformSlug === "tumblr") {
          api.post(`/websites/${siteId}/syndication/tumblr/${res.data.id}/connect/`)
            .then((oauthRes) => {
              if (oauthRes.data.auth_url) {
                const popup = window.open(oauthRes.data.auth_url, "_blank", "width=600,height=700");
                const check = setInterval(() => {
                  if (!popup || popup.closed) {
                    clearInterval(check);
                    api.get(`/websites/${siteId}/syndication/accounts/`)
                      .then((r) => setAccounts(r.data.results || r.data));
                  }
                }, 1000);
              }
            })
            .catch((err) => alert(err.response?.data?.error || "Account created. Click 'Connect with Tumblr' to authorize."));
        }
      })
      .finally(() => setConnecting(false));
  };

  const handleDisconnect = (accountId) => {
    api.delete(`/websites/${siteId}/syndication/accounts/${accountId}/`).then(() => {
      setAccounts((prev) => prev.filter((a) => a.id !== accountId));
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading platforms...</span></div>;
  }

  const grouped = {};
  platforms.forEach((p) => {
    if (!grouped[p.platform_type]) grouped[p.platform_type] = [];
    grouped[p.platform_type].push(p);
  });

  const accountByPlatform = {};
  accounts.forEach((a) => { accountByPlatform[a.platform] = a; });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Connected" value={accounts.filter((a) => a.is_connected).length} icon={Plug} color="text-green-400" />
        <StatCard label="Available" value={platforms.length} icon={Globe} color="text-blue-400" />
        <StatCard label="Posts Sent" value={accounts.reduce((sum, a) => sum + (a.posts_count || 0), 0)} icon={Send} />
      </div>

      {Object.entries(grouped).map(([type, plats]) => (
        <div key={type}>
          <h3 className="text-sm font-medium text-gray-400 mb-3 capitalize">{type} Platforms</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {plats.map((platform) => {
              const account = accountByPlatform[platform.id];
              const IconComp = ICON_MAP[platform.icon] || Globe;
              const tier = TIER_LABELS[platform.tier] || TIER_LABELS[3];
              const helpInfo = PLATFORM_HELP[platform.slug] || {};
              const isManual = !platform.supports_api;
              return (
                <div key={platform.id} className={`bg-dark-300 rounded-xl border p-4 transition-colors ${account ? "border-copper/30" : "border-dark-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <IconComp size={18} className={account ? "text-copper" : "text-gray-500"} />
                      <span className="font-medium text-sm">{platform.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isManual && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800/40 text-gray-400">Manual</span>
                      )}
                      {platform.tier && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${tier.bg} ${tier.color}`}>{tier.label}</span>
                      )}
                      {platform.supports_api && (
                        <span className="text-[10px] text-copper bg-copper/10 px-1.5 py-0.5 rounded">API</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-3 text-[10px] text-gray-500">
                    {platform.da_score > 0 && (
                      <span className="flex items-center gap-1">
                        <Shield size={10} /> DA {platform.da_score}
                      </span>
                    )}
                    {platform.link_type && (
                      <span className={platform.link_type === "dofollow" ? "text-green-500" : "text-gray-500"}>
                        {platform.link_type}
                      </span>
                    )}
                    {platform.recommended_frequency && (
                      <span className="flex items-center gap-1">
                        <Clock size={10} /> {platform.recommended_frequency}
                      </span>
                    )}
                  </div>
                  {platform.best_for && (
                    <div className="text-[10px] text-gray-600 mb-3 italic">Best for: {platform.best_for}</div>
                  )}
                  {account ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle size={12} className={account.is_connected ? "text-green-400" : "text-amber-400"} />
                        <span className={`text-xs ${account.is_connected ? "text-green-400" : "text-amber-400"}`}>
                          {account.is_connected ? `Connected as ${account.account_name}` : `${account.account_name} — needs auth`}
                        </span>
                      </div>
                      {/* Blogger: show Connect with Google if no OAuth token */}
                      {platform.slug === "blogger" && !account.settings?.blog_id && (
                        <button
                          onClick={() => {
                            api.post(`/websites/${siteId}/syndication/blogger/${account.id}/connect/`)
                              .then((res) => {
                                if (res.data.auth_url) {
                                  const popup = window.open(res.data.auth_url, "_blank", "width=600,height=700");
                                  const check = setInterval(() => {
                                    if (!popup || popup.closed) {
                                      clearInterval(check);
                                      api.get(`/websites/${siteId}/syndication/accounts/`)
                                        .then((r) => setAccounts(r.data.results || r.data));
                                    }
                                  }, 1000);
                                }
                              })
                              .catch((err) => alert(err.response?.data?.error || "Failed to start Google OAuth"));
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 mb-2 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 transition-colors w-full justify-center"
                        >
                          <Globe size={12} /> Connect with Google
                        </button>
                      )}
                      {/* Tumblr: show Connect with Tumblr if no OAuth access token */}
                      {platform.slug === "tumblr" && !account.has_oauth_token && (
                        <button
                          onClick={() => {
                            api.post(`/websites/${siteId}/syndication/tumblr/${account.id}/connect/`)
                              .then((res) => {
                                if (res.data.auth_url) {
                                  const popup = window.open(res.data.auth_url, "_blank", "width=600,height=700");
                                  const check = setInterval(() => {
                                    if (!popup || popup.closed) {
                                      clearInterval(check);
                                      api.get(`/websites/${siteId}/syndication/accounts/`)
                                        .then((r) => setAccounts(r.data.results || r.data));
                                    }
                                  }, 1000);
                                }
                              })
                              .catch((err) => alert(err.response?.data?.error || "Failed to start Tumblr OAuth"));
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 mb-2 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 transition-colors w-full justify-center"
                        >
                          <Globe size={12} /> Connect with Tumblr
                        </button>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-500">{account.posts_count || 0} posts</span>
                        {account.settings?.blog_id && (
                          <span className="text-[10px] text-gray-600 ml-1">Blog ID: {account.settings.blog_id}</span>
                        )}
                        {account.settings?.blog_name && (
                          <span className="text-[10px] text-gray-600 ml-1">Blog: {account.settings.blog_name}</span>
                        )}
                        <div className="flex-1" />
                        <button onClick={() => handleDisconnect(account.id)}
                          className="text-xs text-gray-500 hover:text-red-400 transition-colors">Disconnect</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {showConnect === platform.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder={helpInfo.placeholder || "Account name / username"}
                            value={connectForm.account_name}
                            onChange={(e) => setConnectForm((f) => ({ ...f, account_name: e.target.value }))}
                            className="w-full bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none"
                          />
                          {platform.supports_api && platform.slug !== "blogger" && platform.slug !== "tumblr" && (
                            <>
                              {(helpInfo.apiKeyLabel !== "") && (
                                <input type="password" placeholder={helpInfo.apiKeyLabel || "API Key"} value={connectForm.api_key}
                                  onChange={(e) => setConnectForm((f) => ({ ...f, api_key: e.target.value }))}
                                  className="w-full bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
                              )}
                              {(helpInfo.tokenLabel !== "") && (
                                <input type="password" placeholder={helpInfo.tokenLabel || "Access Token"} value={connectForm.access_token}
                                  onChange={(e) => setConnectForm((f) => ({ ...f, access_token: e.target.value }))}
                                  className="w-full bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
                              )}
                              {(helpInfo.extraFields || []).map((ef) => (
                                <input key={ef.key} type="text"
                                  placeholder={ef.placeholder || ef.label}
                                  value={connectForm.extraSettings[ef.key] || ""}
                                  onChange={(e) => setConnectForm((f) => ({
                                    ...f, extraSettings: { ...f.extraSettings, [ef.key]: e.target.value }
                                  }))}
                                  className="w-full bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
                              ))}
                            </>
                          )}
                          {platform.slug === "blogger" && (
                            <p className="text-[10px] text-amber-400/80 flex items-start gap-1">
                              <Info size={10} className="shrink-0 mt-0.5" /> After adding, you'll be redirected to Google to authorize Blogger access.
                            </p>
                          )}
                          {platform.slug === "tumblr" && (
                            <>
                              <input type="password" placeholder="OAuth Consumer Key (from tumblr.com/oauth/apps)" value={connectForm.api_key}
                                onChange={(e) => setConnectForm((f) => ({ ...f, api_key: e.target.value }))}
                                className="w-full bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
                              <input type="password" placeholder="OAuth Consumer Secret" value={connectForm.extraSettings?.api_secret || ""}
                                onChange={(e) => setConnectForm((f) => ({ ...f, extraSettings: { ...f.extraSettings, api_secret: e.target.value } }))}
                                className="w-full bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
                              <p className="text-[10px] text-amber-400/80 flex items-start gap-1">
                                <Info size={10} className="shrink-0 mt-0.5" /> After adding, you'll be redirected to Tumblr to authorize publishing access.
                              </p>
                            </>
                          )}
                          {helpInfo.help && (
                            <p className="text-[10px] text-gray-600 flex items-start gap-1">
                              <Info size={10} className="shrink-0 mt-0.5" /> {helpInfo.help}
                            </p>
                          )}
                          <div className="flex gap-1">
                            <button onClick={() => handleConnect(platform.id, platform.slug)} disabled={connecting}
                              className="px-2 py-1 text-xs bg-copper text-dark rounded font-medium disabled:opacity-50">
                              {connecting ? "..." : platform.slug === "blogger" ? "Add & Connect Google" : platform.slug === "tumblr" ? "Add & Connect Tumblr" : isManual ? "Add" : "Connect"}
                            </button>
                            <button onClick={() => setShowConnect(null)} className="px-2 py-1 text-xs text-gray-400 hover:text-white">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <button onClick={() => { setShowConnect(platform.id); setConnectForm({ account_name: "", api_key: "", access_token: "", extraSettings: {} }); }}
                            className="text-xs text-copper hover:underline">Connect Account</button>
                          {platform.signup_url && (
                            <a href={platform.signup_url} target="_blank" rel="noopener noreferrer"
                              className="text-[10px] text-gray-500 hover:text-copper flex items-center gap-1 transition-colors">
                              <ExternalLink size={10} /> Sign up
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ======== TAB: SCHEDULES ======== */
function SchedulesTab({ siteId }) {
  const [schedules, setSchedules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    account: "", delay_hours: 24, auto_syndicate_new_pages: false,
    auto_syndicate_new_content: false, add_canonical: true, add_backlink: true,
  });
  const [creating, setCreating] = useState(false);
  const [togglingAll, setTogglingAll] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/syndication/schedules/`),
      api.get(`/websites/${siteId}/syndication/accounts/`),
    ])
      .then(([schedRes, accRes]) => {
        setSchedules(schedRes.data.results || schedRes.data);
        setAccounts(accRes.data.results || accRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleCreate = () => {
    if (!newSchedule.account) return;
    setCreating(true);
    api.post(`/websites/${siteId}/syndication/schedules/`, newSchedule)
      .then((res) => {
        setSchedules((prev) => [...prev, res.data]);
        setShowCreate(false);
      })
      .finally(() => setCreating(false));
  };

  const handleToggle = (id, field) => {
    const sched = schedules.find((s) => s.id === id);
    if (!sched) return;
    api.patch(`/websites/${siteId}/syndication/schedules/${id}/`, { [field]: !sched[field] })
      .then((res) => {
        setSchedules((prev) => prev.map((s) => s.id === id ? res.data : s));
      });
  };

  const handleDelete = (id) => {
    api.delete(`/websites/${siteId}/syndication/schedules/${id}/`).then(() => {
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    });
  };

  const allActive = schedules.length > 0 && schedules.every((s) => s.is_active);
  const handleToggleAll = () => {
    const newState = !allActive;
    setTogglingAll(true);
    Promise.all(
      schedules.map((s) =>
        api.patch(`/websites/${siteId}/syndication/schedules/${s.id}/`, { is_active: newState })
      )
    )
      .then((results) => {
        setSchedules((prev) =>
          prev.map((s) => {
            const updated = results.find((r) => r.data.id === s.id);
            return updated ? updated.data : s;
          })
        );
      })
      .finally(() => setTogglingAll(false));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin text-copper" size={24} /><span className="ml-3 text-gray-400">Loading schedules...</span></div>;
  }

  return (
    <div className="space-y-6">
      {schedules.length > 0 && (
        <div className={`flex items-center justify-between p-4 rounded-xl border ${allActive ? "bg-copper/5 border-copper/30" : "bg-dark-300 border-dark-50"}`}>
          <div className="flex items-center gap-3">
            <Zap size={18} className={allActive ? "text-copper" : "text-gray-500"} />
            <div>
              <span className="text-sm font-medium">Auto-Pilot</span>
              <p className="text-[10px] text-gray-500">
                {allActive ? "All syndication rules are active" : "Some rules are paused"}
              </p>
            </div>
          </div>
          <button
            onClick={handleToggleAll}
            disabled={togglingAll}
            className={`relative w-12 h-6 rounded-full transition-colors ${allActive ? "bg-copper" : "bg-dark-50"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${allActive ? "left-6" : "left-0.5"}`} />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Auto-syndicate new content to your connected platforms.
        </p>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 transition-colors">
          <Plus size={16} /> New Rule
        </button>
      </div>

      {showCreate && (
        <div className="bg-dark-300 rounded-xl border border-copper/30 p-4 space-y-3">
          <select value={newSchedule.account} onChange={(e) => setNewSchedule((s) => ({ ...s, account: e.target.value }))}
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none">
            <option value="">Select account...</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.platform_name} — {a.account_name}</option>
            ))}
          </select>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input type="checkbox" checked={newSchedule.auto_syndicate_new_pages}
                onChange={(e) => setNewSchedule((s) => ({ ...s, auto_syndicate_new_pages: e.target.checked }))}
                className="accent-copper" />
              Auto-syndicate new pages
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input type="checkbox" checked={newSchedule.auto_syndicate_new_content}
                onChange={(e) => setNewSchedule((s) => ({ ...s, auto_syndicate_new_content: e.target.checked }))}
                className="accent-copper" />
              Auto-syndicate new content
            </label>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Delay:</span>
              <input type="number" min="0" max="168" value={newSchedule.delay_hours}
                onChange={(e) => setNewSchedule((s) => ({ ...s, delay_hours: parseInt(e.target.value) || 0 }))}
                className="w-16 bg-dark border border-dark-50 rounded px-2 py-1 text-xs text-white focus:border-copper/50 focus:outline-none" />
              <span className="text-xs text-gray-500">hours</span>
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={newSchedule.add_canonical}
                onChange={(e) => setNewSchedule((s) => ({ ...s, add_canonical: e.target.checked }))}
                className="accent-copper" /> Add canonical
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={newSchedule.add_backlink}
                onChange={(e) => setNewSchedule((s) => ({ ...s, add_backlink: e.target.checked }))}
                className="accent-copper" /> Add backlink
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating || !newSchedule.account}
              className="px-4 py-1.5 bg-copper text-dark rounded-lg text-sm font-medium disabled:opacity-50">
              {creating ? "Creating..." : "Create Rule"}
            </button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-1.5 text-gray-400 text-sm hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {schedules.length === 0 ? (
        <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
          <Calendar size={32} className="mx-auto mb-3 text-gray-600" />
          <p>No auto-syndication rules set up.</p>
          <p className="text-xs mt-1">Create rules to automatically syndicate new content.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map((sched) => (
            <div key={sched.id} className={`bg-dark-300 rounded-xl border p-4 ${sched.is_active ? "border-copper/30" : "border-dark-50 opacity-60"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{sched.platform_name}</span>
                  <span className="text-xs text-gray-500">— {sched.account_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleToggle(sched.id, "is_active")}
                    className={`text-xs px-2 py-0.5 rounded ${sched.is_active ? "bg-green-900/30 text-green-400" : "bg-gray-800/40 text-gray-500"}`}>
                    {sched.is_active ? "Active" : "Paused"}
                  </button>
                  <button onClick={() => handleDelete(sched.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className={sched.auto_syndicate_new_pages ? "text-copper" : "text-gray-600"}>
                  {sched.auto_syndicate_new_pages ? "Auto: New Pages" : ""}
                </span>
                <span className={sched.auto_syndicate_new_content ? "text-copper" : "text-gray-600"}>
                  {sched.auto_syndicate_new_content ? "Auto: New Content" : ""}
                </span>
                <span className="text-gray-500">Delay: {sched.delay_hours}h</span>
                {sched.add_canonical && <span className="text-gray-500">+ Canonical</span>}
                {sched.add_backlink && <span className="text-gray-500">+ Backlink</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ======== MAIN COMPONENT ======== */
export default function ContentSyndication({ selectedSite }) {
  const [activeTab, setActiveTab] = useState("posts");
  const siteId = selectedSite?.id;

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to manage content syndication.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-mono">
          <span className="text-copper">Content</span> Syndication
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          AI-powered content syndication across multiple platforms with backlinks
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"
              }`}>
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "posts" && <PostsTab siteId={siteId} />}
      {activeTab === "accounts" && <AccountsTab siteId={siteId} />}
      {activeTab === "schedules" && <SchedulesTab siteId={siteId} />}
    </div>
  );
}
